import { Injectable } from '@angular/core';
import { AndarService } from './andar.service';
import { AtaService } from './ata.service';
import { BrigadistaService } from './brigadista.service';
import { AtividadeService } from './atividade.service';
import { CategoriaEquipamentoService } from './categoria-equipamento.service';
import { AtividadeGrupoService } from './atividade-grupo.service';
import { EquipamentoService } from './equipamento.service';
import { OcorrenciaService } from './ocorrencia.service';
import { PendenciaService } from './pendencia.service';
import { TurnoService } from './turno.service';
import { UsuarioService } from './usuario.service';
import { firstValueFrom, Observable } from 'rxjs';
import * as moment from 'moment';
import { ToastController } from '@ionic/angular';
import { PlantaoService } from './plantao.service';
import { TipoEquipamentoService } from './tipo-equipamento.service';
import { PlantaoFormularioService } from './plantao-formulario.service';
import { ResponsavelService } from './responsavel.service';
import { RotaFugaService } from './rota-fuga.service';
import { PendenciaMotivoService } from './pendencia-motivo.service';
import { AlmoxarifadoService } from './almoxarifado.service';
import { EstoqueService } from './estoque.service';
import { TipoMotivoService } from './tipo-motivo.service';
import { PontoEncontroService } from './ponto-encontro.service';
import { BlocoNotaService } from './bloco-nota.service';
import { AtendimentoService } from './atendimento.service';
import { ListaReposicaoItemService } from './lista-reposicao-item.service';
import { StorageService } from '../alcance/services/storage.service';
import { SyncStateDBService, SyncStateRecord } from '../alcance/services/sync-state-db.service';
import * as Sentry from '@sentry/angular';

type SyncJob = {
    resource: string;
    run: () => Promise<void>;
};

@Injectable({
    providedIn: 'root'
})
export class SincService {
    isSinc: boolean = false;
    private syncErrorToastOpen: boolean = false;

    private readonly legacyUpdateKey: string = 'dt_atualizacao';
    private readonly updatePrefix: string = 'dt_atualizacao_';
    private readonly syncLimit: number = 200;
    private readonly ataSyncLimit: number = 50;
    private readonly plantaoSyncLimit: number = 50;

    private readonly resources: string[] = [
        'andar',
        'rota',
        'ata',
        'brigadista',
        'categoria_equipamento',
        'atividade_grupo',
        'tipo_equipamento',
        'tipo_motivo',
        'ocorrencia',
        'atendimento',
        'turno',
        'usuario',
        'pendencia',
        'equipamento',
        'atividade',
        'plantao',
        'plantao_formulario',
        'responsavel',
        'pendencia_motivo',
        'almoxarifado',
        'ponto_encontro',
        'estoque',
        'lista_reposicao_item',
        'bloco_nota',
    ];

    private readonly defaultHeaders = {
        showLoader: 'false',
        showError: 'false',
        awaitLocalSync: 'true'
    };

    private retryTimer: any = null;
    private retryAttempt: number = 0;

    constructor(
        private storage: StorageService,
        private syncState: SyncStateDBService,
        private toast: ToastController,
        private usuario: UsuarioService,
        private turno: TurnoService,
        private plantao: PlantaoService,
        private ponto: PontoEncontroService,
        private plantaoFormulario: PlantaoFormularioService,
        private pendencia: PendenciaService,
        private ocorrencia: OcorrenciaService,
        private atendimento: AtendimentoService,
        private pendenciaMotivo: PendenciaMotivoService,
        private equipamento: EquipamentoService,
        private categoria: CategoriaEquipamentoService,
        private atividadeGrupo: AtividadeGrupoService,
        private tipo: TipoEquipamentoService,
        private motivo: TipoMotivoService,
        private brigadista: BrigadistaService,
        private atividade: AtividadeService,
        private ata: AtaService,
        private responsavel: ResponsavelService,
        private almoxarifado: AlmoxarifadoService,
        private estoque: EstoqueService,
        private listaReposicaoItem: ListaReposicaoItemService,
        private blocoNota: BlocoNotaService,
        private rota: RotaFugaService,
        private andar: AndarService,
    ) {}

    private getUpdateKey(resource: string){
        return `${this.updatePrefix}${resource}`;
    }

    private getBaseParams(dtAtualizacao: string | null, page: number = 1, extras: any = {}, limit: number = this.syncLimit){
        return {
            limit,
            page,
            dt_atualizacao: dtAtualizacao,
            ...extras
        };
    }

    private hasNextPage(response: any): boolean{
        return !!(
            response && (
                response.next_page_url ||
                response.next_page ||
                (response.current_page && response.last_page && response.current_page < response.last_page)
            )
        );
    }

    private async tokenSalvoExpirado(margemMinutos: number = 2): Promise<boolean>{
        const token = await this.storage.get('user');
        if(!token?.expires_at){
            return false;
        }

        const expiraEm = (moment as any)(token.expires_at);
        if(!expiraEm.isValid()){
            return false;
        }

        return expiraEm.isSameOrBefore((moment as any)().add(margemMinutos, 'minutes'));
    }

    private async dropAllTables(){
        await Promise.all([
            this.andar.database.dropTable(),
            this.rota.database.dropTable(),
            this.ata.database.dropTable(),
            this.brigadista.database.dropTable(),
            this.categoria.database.dropTable(),
            this.tipo.database.dropTable(),
            this.motivo.database.dropTable(),
            this.ocorrencia.database.dropTable(),
            this.atendimento.database.dropTable(),
            this.turno.database.dropTable(),
            this.usuario.database.dropTable(),
            this.pendencia.database.dropTable(),
            this.equipamento.database.dropTable(),
            this.atividade.database.dropTable(),
            this.atividadeGrupo.database.dropTable(),
            this.plantao.database.dropTable(),
            this.plantaoFormulario.database.dropTable(),
            this.responsavel.database.dropTable(),
            this.pendenciaMotivo.database.dropTable(),
            this.almoxarifado.database.dropTable(),
            this.estoque.database.dropTable(),
            this.listaReposicaoItem.database.dropTable(),
            this.blocoNota.database.dropTable(),
            this.ponto.database.dropTable(),
        ]);
    }

    async resetarSincronizacao(): Promise<void>{
        await this.storage.del(this.legacyUpdateKey);
        await Promise.all(this.resources.map((resource) => this.storage.del(this.getUpdateKey(resource))));
        await this.syncState.resetResources(this.resources);
        await this.dropAllTables();
    }

    /**
     * Sincroniza um recurso em páginas.
     * O cursor só avança quando todas as páginas do recurso terminam.
     * Se o app fechar no meio, continua a partir da próxima página salva em sync_state.
     */
    private async syncResourcePaged(
        resource: string,
        subscriber: (dtAtualizacao: string | null, page: number) => Observable<any>
    ): Promise<void>{
        const legacyDate = await this.storage.get(this.getUpdateKey(resource));
        const state: SyncStateRecord = await this.syncState.getState(resource, legacyDate ?? null);

        const cursor = state.cursor ?? legacyDate ?? null;
        const startedCursor = (moment as any)().format('YYYY-MM-DD HH:mm:ss');
        let page = state.status === 'running' || state.status === 'error' ? (state.page || 1) : 1;
        let hasMore = true;

        await this.syncState.markRunning(resource, cursor, page);

        while(hasMore){
            const response = await firstValueFrom(subscriber(cursor, page));
            hasMore = this.hasNextPage(response);

            // Sentry.addBreadcrumb({
            //     category: 'sync',
            //     level: 'info',
            //     message: resource,
            //     data: {
            //         page,
            //         rows: Array.isArray(response?.data) ? response.data.length : null,
            //         hasMore,
            //     }
            // });

            if(hasMore){
                page++;
                await this.syncState.markPageDone(resource, page);
            }
        }

        await this.syncState.markDone(resource, startedCursor);
        await this.storage.set(this.getUpdateKey(resource), startedCursor);
    }

    /**
     * Estoque possui dois fluxos no código atual:
     * - GET paginado para gravar dados ativos.
     * - /sync para tratar remoções/alterações específicas.
     *
     * Aqui a paginação é aplicada somente ao GET. O /sync fica uma chamada separada
     * após as páginas, mantendo compatibilidade com o backend atual.
     */
    private async syncEstoque(): Promise<void>{
        const legacyDate = await this.storage.get(this.getUpdateKey('estoque'));
        const stateBefore = await this.syncState.getState('estoque', legacyDate ?? null);
        const cursorBefore = stateBefore.cursor ?? legacyDate ?? null;

        await this.syncResourcePaged('estoque', (dtAtualizacao, page) => {
            return this.estoque.get(
                null,
                this.getBaseParams(dtAtualizacao, page, {'with[]': ['almoxarifado', 'equipamento.tipos']}),
                this.defaultHeaders
            );
        });

        await firstValueFrom(
            this.estoque.sync(null,{dt_atualizacao: cursorBefore},this.defaultHeaders)
        );
    }

    private async syncBlocoNota(): Promise<void>{
        const legacyDate = await this.storage.get(this.getUpdateKey('bloco_nota'));
        const state = await this.syncState.getState('bloco_nota', legacyDate ?? null);
        const cursor = state.cursor ?? legacyDate ?? null;
        await this.syncResourcePaged('bloco_nota', (dtAtualizacao, page) => this.blocoNota.get(null, this.getBaseParams(dtAtualizacao, page, {'with[]': ['equipamento', 'itens.itemReposicao']}), this.defaultHeaders));
        await firstValueFrom(this.blocoNota.sync(null, {dt_atualizacao: cursor}, this.defaultHeaders));
    }

    private async syncListaReposicaoItem(): Promise<void>{
        const legacyDate = await this.storage.get(this.getUpdateKey('lista_reposicao_item'));
        const state = await this.syncState.getState('lista_reposicao_item', legacyDate ?? null);
        const cursor = state.cursor ?? legacyDate ?? null;
        await this.syncResourcePaged('lista_reposicao_item', (dtAtualizacao, page) => this.listaReposicaoItem.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders));
        await firstValueFrom(this.listaReposicaoItem.sync(null, {dt_atualizacao: cursor}, this.defaultHeaders));
    }

    private getSyncJobs(): SyncJob[]{
        return [
            {
                resource: 'andar',
                run: () => this.syncResourcePaged('andar', (dtAtualizacao, page) => this.andar.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'rota',
                run: () => this.syncResourcePaged('rota', (dtAtualizacao, page) => this.rota.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'ata',
                run: () => this.syncResourcePaged('ata', (dtAtualizacao, page) => this.ata.get(null, this.getBaseParams(dtAtualizacao, page, {'with[]': ['assinaturas']}, this.ataSyncLimit), this.defaultHeaders))
            },
            {
                resource: 'brigadista',
                run: () => this.syncResourcePaged('brigadista', (dtAtualizacao, page) => this.brigadista.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'categoria_equipamento',
                run: () => this.syncResourcePaged('categoria_equipamento', (dtAtualizacao, page) => this.categoria.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'atividade_grupo',
                run: () => this.syncResourcePaged('atividade_grupo', (dtAtualizacao, page) => this.atividadeGrupo.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'tipo_equipamento',
                run: () => this.syncResourcePaged('tipo_equipamento', (dtAtualizacao, page) => this.tipo.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'tipo_motivo',
                run: () => this.syncResourcePaged('tipo_motivo', (dtAtualizacao, page) => this.motivo.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'ocorrencia',
                run: () => this.syncResourcePaged('ocorrencia', (dtAtualizacao, page) => this.ocorrencia.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'atendimento',
                run: () => this.syncResourcePaged('atendimento', (dtAtualizacao, page) => this.atendimento.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'turno',
                run: () => this.syncResourcePaged('turno', (dtAtualizacao, page) => this.turno.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'usuario',
                run: () => this.syncResourcePaged('usuario', (dtAtualizacao, page) => this.usuario.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'pendencia',
                run: () => this.syncResourcePaged('pendencia', (dtAtualizacao, page) => this.pendencia.get(null, this.getBaseParams(dtAtualizacao, page, {'with[]': ['andar'], status: 'P'}), this.defaultHeaders))
            },
            {
                resource: 'equipamento',
                run: () => this.syncResourcePaged('equipamento', (dtAtualizacao, page) => this.equipamento.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'atividade',
                run: () => this.syncResourcePaged('atividade', (dtAtualizacao, page) => this.atividade.get(null, this.getBaseParams(dtAtualizacao, page, {'with[]': ['equipamento.tipos', 'execucao', 'equipamentosInspecao.equipamento.tipos', 'grupos']}), this.defaultHeaders))
            },
            {
                resource: 'plantao',
                run: () => this.syncResourcePaged('plantao', (dtAtualizacao, page) => this.plantao.get(null, this.getBaseParams(dtAtualizacao, page, {'with[]': ['ocorrencias', 'atividades', 'atendimentos', 'liberacoes', 'formularios', 'assinaturas']}, this.plantaoSyncLimit), this.defaultHeaders))
            },
            {
                resource: 'plantao_formulario',
                run: () => this.syncResourcePaged('plantao_formulario', (dtAtualizacao, page) => this.plantaoFormulario.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'responsavel',
                run: () => this.syncResourcePaged('responsavel', (dtAtualizacao, page) => this.responsavel.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'pendencia_motivo',
                run: () => this.syncResourcePaged('pendencia_motivo', (dtAtualizacao, page) => this.pendenciaMotivo.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'almoxarifado',
                run: () => this.syncResourcePaged('almoxarifado', (dtAtualizacao, page) => this.almoxarifado.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'ponto_encontro',
                run: () => this.syncResourcePaged('ponto_encontro', (dtAtualizacao, page) => this.ponto.get(null, this.getBaseParams(dtAtualizacao, page), this.defaultHeaders))
            },
            {
                resource: 'estoque',
                run: () => this.syncEstoque()
            },
            {
                resource: 'lista_reposicao_item',
                run: () => this.syncListaReposicaoItem()
            },
            {
                resource: 'bloco_nota',
                run: () => this.syncBlocoNota()
            },
        ];
    }

    private getRetryDelay(status?: number){
        const baseDelay = status == 429 ? 15000 : 8000;
        return Math.min(baseDelay * (this.retryAttempt + 1), 120000);
    }

    private queueRetry(status?: number){
        if(status === 401 || status === 403){
            return;
        }

        if(this.retryTimer){
            return;
        }

        const delay = this.getRetryDelay(status);
        this.retryAttempt++;

        this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            this.sincroniza();
        }, delay);
    }

    private async presentSyncErrorToast(){
        if(this.syncErrorToastOpen){
            return;
        }

        this.syncErrorToastOpen = true;

        // const toast = await this.toast.create({
        //     mode: 'ios',
        //     color: 'warning',
        //     icon: 'warning',
        //     message: 'Ocorreu um erro durante a sincronização dos dados. O app tentará continuar automaticamente.',
        //     duration: 5000
        // });

        // await toast.present();
        // await toast.onDidDismiss();
        this.syncErrorToastOpen = false;
    }

    private isAuthError(error: any): boolean{
        return error?.status === 401 || error?.status === 403;
    }

    private async handleAuthSyncError(error: any): Promise<void>{
        if(this.retryTimer){
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }

        this.retryAttempt = 0;

        // await this.toast.create({
        //     mode: 'ios',
        //     color: 'warning',
        //     icon: 'lock-closed',
        //     message: error?.status === 403
        //         ? 'Sua sessão não tem permissão para sincronizar alguns dados. A sincronização foi pausada.'
        //         : 'Sua sessão expirou. Faça login novamente para continuar a sincronização.',
        //     duration: 6000
        // }).then((toast) => toast.present());
    }

    async sincroniza(){
        if(this.retryTimer){
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }

        if(this.isSinc){
            return;
        }

        this.isSinc = true;

        const failedJobs: {resource: string; error: any}[] = [];

        try {
            const legacyDate = await this.storage.get(this.legacyUpdateKey);

            //LIMPA OS DADOS DE OUTRO LOGIN
            if(!legacyDate){
                await Promise.all(this.resources.map((resource) => this.storage.del(this.getUpdateKey(resource))));
                await this.syncState.resetResources(this.resources);
                await this.dropAllTables();
            }

            if(await this.tokenSalvoExpirado()){
                await this.handleAuthSyncError({status: 401});
                return;
            }

            for(const job of this.getSyncJobs()){
                try{
                    if(await this.tokenSalvoExpirado()){
                        await this.handleAuthSyncError({status: 401});
                        return;
                    }

                    await job.run();
                }catch(error: any){
                    // Sentry.withScope((scope) => {
                    //     scope.setTag('sync_resource', job.resource);
                    //     scope.setContext('sync_state', {
                    //         retryAttempt: this.retryAttempt,
                    //         status: error?.status || null,
                    //     });
                    //     Sentry.captureException(error);
                    // });

                    await this.syncState.markError(job.resource, error, this.getRetryDelay(error?.status) / 1000);

                    if(this.isAuthError(error)){
                        await this.handleAuthSyncError(error);
                        return;
                    }

                    failedJobs.push({resource: job.resource, error});
                }
            }

            if(failedJobs.length > 0){
                await this.presentSyncErrorToast();
                this.queueRetry(failedJobs[0].error?.status);
                return;
            }

            this.retryAttempt = 0;
            await this.storage.set(this.legacyUpdateKey, (moment as any)().format('YYYY-MM-DD HH:mm:ss'));
        } finally {
            this.isSinc = false;
        }
    }
}
